import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import type { Character, CombatFightSummary, CombatLogEntry, Item, Spell } from '../types';
import { BackButton } from '../components/BackButton';
import { ConfirmDialog, QuantityField } from '../components/ConfirmDialog';
import { GameNpcsSection } from '../components/GameNpcsSection';
import { paths } from '../navigation/paths';
import { barPercent } from '../utils/avatars';
import { ClassMark } from '../components/Pictogram';
import { armorPool, bonusSources, formatItemCost, isArmorBroken, isArmorDamaged, itemDamageBonus, itemHealthBonus, itemKitChips, itemManaBonus, itemMovementBonus, isConsumablePotion, isCurrencyItem, isPotionItem, isStackableItem, potionHasLastingEffect, potionRestoreSummary, stackInventoryItems, totalAttackDamage, totalMovement, vitalWithItemBonus, type BuffBonuses, type ItemChip } from '../utils/itemStats';
import { createCharacterBuff, repairCharacterArmor, revokeCharacterEffect, triggerCharacterEffect } from '../api/games';
import { CustomBuffDialog } from '../components/CustomBuffDialog';
import { fetchCombatLog } from '../api/combat';
import { errorMessage } from '../api/client';
import { burnTriggerAmount, isBurnEffect, isRepeatHeal, repeatHealAmount, repeatHealCharges } from '../utils/spellEffect';
import { Pictogram } from '../components/Pictogram';
import { CombatLogDrawer } from '../components/CombatLogDrawer';
import { mergeRosterCycle, rotateRosterTo } from '../input/overviewGamepad';
import { useGamepadNavigation } from '../input/useGamepadNavigation';

function itemDisplayName(item: Pick<Item, 'name'>) {
  const match = item.name.match(/^(\p{Emoji}+)\s+(.+)$/u);
  return match ? match[2] : item.name;
}

function kitEffectCaption(effect: string, name = '', description = '') {
  const raw = String(effect || '').trim();
  if (!raw) return '';
  const match = raw.match(/-?\d+(?:\.\d+)?/);
  if (!match) return raw;
  const blob = `${name} ${description} ${raw}`.toLowerCase();
  const kindLabel = /mana|mp/.test(blob) ? 'Mana' : /heil|heal/.test(blob) ? 'Heilung' : 'Schaden';
  const amount = Math.abs(Number(match[0]));
  if (!Number.isFinite(amount)) return raw;
  return `${kindLabel} ${amount}`;
}

function kitEffectParts(effect: string, name = '', description = '') {
  const caption = kitEffectCaption(effect, name, description);
  const parsed = caption.match(/^(Schaden|Heilung|Mana)\s+(\d+(?:\.\d+)?)$/);
  if (!parsed) {
    return { caption, kind: null as ItemChip['kind'] | null, icon: '', amount: '', label: caption };
  }
  const kind = parsed[1] === 'Heilung' ? 'heal' as const : parsed[1] === 'Mana' ? 'mana' as const : 'damage' as const;
  const icon = kind === 'heal' ? '❤' : kind === 'mana' ? '✨' : '⚔';
  return { caption, kind, icon, amount: parsed[2], label: caption };
}

function readableSpellEffect(evaluated: string, caption: string) {
  const text = String(evaluated || '').trim();
  if (!text) return caption;
  if (/[a-zA-ZäöüÄÖÜ]/.test(text)) return text;
  return caption || text;
}

function isHealSpell(spell: Pick<Spell, 'name' | 'description' | 'effect'>, evaluatedEffect = '') {
  const blob = `${spell.name} ${spell.description} ${evaluatedEffect || spell.effect || ''}`.toLowerCase();
  if (/schaden|damage/.test(blob)) return false;
  return /heil|heal/.test(blob);
}

function effectAmount(text: string) {
  const match = String(text || '').match(/-?\d+(?:\.\d+)?/);
  if (!match) return 0;
  const amount = Math.abs(Number(match[0]));
  return Number.isFinite(amount) ? Math.round(amount) : 0;
}

type NumberEditPopup = {
  mode: 'level-up' | 'set-level' | 'bulk-level-up';
  title: string;
  confirmLabel: string;
  characterId?: number;
  charName: string;
  value: number;
  min: number;
  max: number;
};

function itemKitCaption(item: Item, vitals?: { maxHp: number; maxMana: number } | null) {
  const bits = itemKitChips(item, vitals).map((chip) => chip.label);
  return bits.length ? `${itemDisplayName(item)} · ${bits.join(', ')}` : itemDisplayName(item);
}

function itemKitExtra(item: Item, vitals?: { maxHp: number; maxMana: number } | null) {
  const caption = itemKitCaption(item, vitals);
  const name = itemDisplayName(item);
  if (caption === name) return '';
  if (caption.startsWith(`${name} · `)) return caption.slice(name.length + 3);
  return caption;
}

function uniqueById<T extends { id: number }>(items: T[]): T[] {
  return Array.from(new Map(items.map((item) => [item.id, item])).values());
}

function startHoldRepeat(apply: () => void) {
  apply();
  let intervalId: number | null = null;
  const timeoutId = window.setTimeout(() => {
    intervalId = window.setInterval(apply, 70);
  }, 320);
  return () => {
    window.clearTimeout(timeoutId);
    if (intervalId != null) window.clearInterval(intervalId);
  };
}


type DamageFn = (formula: string | undefined, character: Character, fallback: string) => number;

function characterVitals(playerChar: Character, calculateDamage: DamageFn) {
  const formulaHp = calculateDamage(playerChar.health_formula, playerChar, '50+constitution*5');
  const formulaMana = calculateDamage(playerChar.mana_formula, playerChar, '20+intelligence*3');
  const sources = bonusSources(playerChar);
  const hp = vitalWithItemBonus(playerChar.health, formulaHp, itemHealthBonus(sources));
  const mp = vitalWithItemBonus(playerChar.mana || 0, formulaMana, itemManaBonus(sources));
  const maxHp = hp.max;
  const maxMana = mp.max;
  const currentHp = hp.current;
  const currentMana = mp.current;
  const baseDmg = calculateDamage(playerChar.damage_formula, playerChar, '5+strength*0.5');
  const baseMove = calculateDamage(playerChar.movement_formula, playerChar, '3+dexterity*0.1');
  const armor = armorPool(sources);
  return {
    maxHp,
    maxMana,
    currentHp,
    currentMana,
    hpPct: barPercent(currentHp, maxHp),
    manaPct: barPercent(currentMana, maxMana),
    damage: totalAttackDamage(baseDmg, sources),
    damageBonus: itemDamageBonus(sources),
    movement: totalMovement(baseMove, sources),
    movementBonus: itemMovementBonus(sources),
    armor: armor.current,
    armorMax: armor.max,
    armorPct: barPercent(armor.current, armor.max),
  };
}

function KitChipView({ chip }: { chip: ItemChip }) {
  return (
    <span className={`kit-card-chip is-${chip.kind}${chip.label === 'Kaputt' ? ' is-broken' : ''}`}>
      <span aria-hidden>{chip.icon}</span> {chip.label}
    </span>
  );
}

function bindDropHandlers(onDrop: (itemId: number, spellId: number) => void) {
  return {
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      e.currentTarget.classList.add('drag-over');
    },
    onDragLeave: (e: React.DragEvent<HTMLElement>) => {
      e.currentTarget.classList.remove('drag-over');
    },
    onDrop: (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove('drag-over');
      const itemId = parseInt(e.dataTransfer.getData('itemId'), 10);
      const spellId = parseInt(e.dataTransfer.getData('spellId'), 10);
      onDrop(itemId, spellId);
    },
  };
}

export function GameOverviewPage() {
  const navigate = useNavigate();
  const { gameId: gameIdParam } = useParams();
  const routeGameId = gameIdParam ? parseInt(gameIdParam, 10) : null;
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [rosterCycle, setRosterCycle] = useState<number[]>([]);
  const rosterListRef = useRef<HTMLDivElement | null>(null);
  const rosterTops = useRef(new Map<number, number>());
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addSpellOpen, setAddSpellOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(true);
  const [combatLogOpen, setCombatLogOpen] = useState(false);
  const [combatLogEntries, setCombatLogEntries] = useState<CombatLogEntry[]>([]);
  const [combatLogFights, setCombatLogFights] = useState<CombatFightSummary[]>([]);
  const [combatLogBusy, setCombatLogBusy] = useState(false);
  const [tooltipPopup, setTooltipPopup] = useState<{ content: string } | null>(null);
  const [vitalEditPopup, setVitalEditPopup] = useState<{
    type: 'health' | 'mana';
    characterId: number;
    value: number;
    max: number;
    charName: string;
  } | null>(null);
  const [numberEditPopup, setNumberEditPopup] = useState<NumberEditPopup | null>(null);
  const [itemToRemove, setItemToRemove] = useState<{
    characterId: number;
    itemId: number;
    characterItemId?: number;
    itemName: string;
    charName: string;
    maxCount: number;
  } | null>(null);
  const [removeQty, setRemoveQty] = useState(1);
  const [goldToSpend, setGoldToSpend] = useState<{
    characterId: number;
    characterItemId: number;
    itemName: string;
    maxCount: number;
  } | null>(null);
  const [spendQty, setSpendQty] = useState(1);
  const [buffTarget, setBuffTarget] = useState<Character | null>(null);
  const [buffBusy, setBuffBusy] = useState(false);
  const [buffError, setBuffError] = useState<string | null>(null);
  const holdRepeatStopRef = useRef<(() => void) | null>(null);
  const app = useApp();

  const stopHoldRepeat = () => {
    holdRepeatStopRef.current?.();
    holdRepeatStopRef.current = null;
  };

  const beginHoldRepeat = (apply: () => void) => {
    stopHoldRepeat();
    holdRepeatStopRef.current = startHoldRepeat(apply);
  };

  useEffect(() => () => {
    holdRepeatStopRef.current?.();
    holdRepeatStopRef.current = null;
  }, []);

  useEffect(() => {
    if (!addItemOpen && !addSpellOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if ((e.target as Element).closest('.add-dropdown-wrapper')) return;
      setAddItemOpen(false);
      setAddSpellOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [addItemOpen, addSpellOpen]);

  const gameOverview = app.gameOverview;
  const overviewLoadError = app.overviewLoadError;
  const fetchGameOverview = app.fetchGameOverview;
  const currentGameId = routeGameId ?? app.currentGameId;
  const handleLogout = app.handleLogout;
  const handleBulkLevelUp = app.handleBulkLevelUp as (charIds: number[], skillPoints: number) => void;
  const calculateDamage = app.calculateDamage as DamageFn;
  const evaluateFormulasInText = app.evaluateFormulasInText;
  const handleAssignItem = app.handleAssignItem as (charId: number, itemId: number) => void;
  const handleAssignSpell = app.handleAssignSpell as (charId: number, spellId: number) => void;
  const handleRemoveSpell = app.handleRemoveSpell as (charId: number, spellId: number) => void;
  const handleRemoveItem = app.handleRemoveItem as (charId: number, itemId: number, characterItemId?: number, count?: number) => void;
  const handleLevelUp = app.handleLevelUp as (charId: number, skillPoints: number) => void;
  const handleSetLevel = app.handleSetLevel as (charId: number, newLevel: number) => void;
  const handleResetLevel = app.handleResetLevel as (charId: number) => void;
  const handleSetHealthMana = app.handleSetHealthMana as (charId: number, health: number | null, mana: number | null) => void;
  const handleUseCharacterItem = app.handleUseCharacterItem as (charId: number, characterItemId: number, count?: number) => void;
  const setToastMessage = app.setToastMessage as (message: string | null) => void;
  const handleAcceptJoinRequest = app.handleAcceptJoinRequest as (requestId: number) => void;
  const handleRejectJoinRequest = app.handleRejectJoinRequest as (requestId: number) => void;

  const players = gameOverview?.players || [];
  const characters = gameOverview?.characters || [];

  const confirmNumberEdit = (popup: NumberEditPopup) => {
    const value = Math.min(popup.max, Math.max(popup.min, Math.round(Number(popup.value))));
    if (!Number.isFinite(value)) {
      setNumberEditPopup(null);
      return;
    }
    if (popup.mode === 'level-up' && popup.characterId != null) {
      handleLevelUp(popup.characterId, value);
    } else if (popup.mode === 'set-level' && popup.characterId != null) {
      handleSetLevel(popup.characterId, value);
    } else if (popup.mode === 'bulk-level-up') {
      handleBulkLevelUp(characters.map((character) => character.id), value);
    }
    setNumberEditPopup(null);
  };

  const castSpell = (char: Character, spell: Spell) => {
    const vitals = characterVitals(char, calculateDamage);
    const cost = spell.mana_cost ?? 0;
    if (vitals.currentMana < cost) {
      setToastMessage(`Nicht genug Mana für ${itemDisplayName(spell)}`);
      window.setTimeout(() => setToastMessage(null), 3000);
      return;
    }
    const newMana = Math.max(0, vitals.currentMana - cost);
    const evaluatedEffect = evaluateFormulasInText(spell.effect, char);
    const heal = isHealSpell(spell, evaluatedEffect) ? effectAmount(evaluatedEffect) : 0;
    const newHealth = heal > 0 ? Math.min(vitals.maxHp, vitals.currentHp + heal) : null;
    handleSetHealthMana(char.id, newHealth, newMana);
  };

  useEffect(() => {
    if (players.length === 0) {
      setSelectedPlayerId(null);
      return;
    }
    if (selectedPlayerId != null && players.some((player) => player.id === selectedPlayerId)) return;
    const firstWithChar = players.find((player) => characters.some((character) => character.player_id === player.id));
    setSelectedPlayerId((firstWithChar || players[0]).id);
  }, [players, characters, selectedPlayerId]);

  useEffect(() => {
    const ids = players.map((player) => player.id);
    setRosterCycle((cycle) => {
      const merged = mergeRosterCycle(cycle, ids);
      const next = selectedPlayerId == null ? merged : rotateRosterTo(merged, selectedPlayerId);
      if (next.length === cycle.length && next.every((id, index) => id === cycle[index])) return cycle;
      return next;
    });
  }, [players, selectedPlayerId]);

  useLayoutEffect(() => {
    const list = rosterListRef.current;
    if (!list) return;
    const rows = Array.from(list.querySelectorAll<HTMLElement>('[data-roster-id]'));
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const previous = rosterTops.current;
    const next = new Map<number, number>();
    rows.forEach((row) => {
      if (typeof row.getAnimations === 'function') {
        row.getAnimations().forEach((animation) => animation.cancel());
      }
      const id = Number(row.getAttribute('data-roster-id'));
      const top = row.offsetTop;
      next.set(id, top);
      const before = previous.get(id);
      if (reduceMotion || before == null || Math.abs(before - top) < 1 || typeof row.animate !== 'function') return;
      const delta = before - top;
      row.style.zIndex = delta < 0 ? '2' : '1';
      const animation = row.animate(
        [
          { transform: `translateY(${delta}px)` },
          { transform: 'translateY(0px)' },
        ],
        { duration: 720, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'backwards' },
      );
      animation.onfinish = () => {
        row.style.zIndex = '';
      };
    });
    rosterTops.current = next;
  }, [rosterCycle]);

  const pageRef = useGamepadNavigation({
    regions: ['roster', 'sheet', 'catalog'],
    profile: 'overview',
    hint: 'LB Charakter · A hinein · B zurück · RB Katalog · Y Kampf',
  });

  if (!gameOverview || !gameOverview.game) {
    return (
      <div className="game-overview medieval" ref={pageRef} data-gamepad-root>
        <div className="loading-container">
          {overviewLoadError ? (
            <>
              <p className="overview-error-text">Fehler beim Laden</p>
              <p className="overview-error-detail">{overviewLoadError}</p>
            </>
          ) : (
            <>
              <div className="spinner">⏳</div>
              <p>Lade Spielübersicht...</p>
            </>
          )}
          <BackButton fallback={paths.masterGames} className="medieval-button">
            Zurück<span className="btn-extra"> zum Admin-Panel</span>
          </BackButton>
          {overviewLoadError && currentGameId && (
            <button
              type="button"
              className="medieval-button"
              style={{ marginTop: 8 }}
              onClick={() => fetchGameOverview(currentGameId)}
            >
              Erneut versuchen
            </button>
          )}
        </div>
      </div>
    );
  }

  const selectedPlayer = players.find((player) => player.id === selectedPlayerId) || null;
  const rosterIds = rosterCycle.length > 0 ? rosterCycle : players.map((player) => player.id);
  const orderedPlayers = rosterIds.flatMap((id) => {
    const player = players.find((entry) => entry.id === id);
    return player ? [player] : [];
  });
  const selectedChar = selectedPlayer
    ? characters.find((character) => character.player_id === selectedPlayer.id) || null
    : null;
  const catalogItems = uniqueById(gameOverview.items || []);
  const catalogSpells = uniqueById(gameOverview.spells || []);
  const assignedGearIds = new Set(
    (selectedChar?.items || []).filter((item) => !isStackableItem(item)).map((item) => item.id),
  );
  const assignedSpellIds = new Set((selectedChar?.spells || []).map((spell) => spell.id));
  const assignableCatalogItems = catalogItems.filter((item) => isStackableItem(item) || !assignedGearIds.has(item.id));

  const assignDropped = (charId: number, itemId: number, spellId: number) => {
    if (itemId) {
      const item = catalogItems.find((entry) => entry.id === itemId);
      const target = characters.find((character) => character.id === charId);
      if (item && !isStackableItem(item) && (target?.items || []).some((owned) => owned.id === itemId)) return;
      handleAssignItem(charId, itemId);
    } else if (spellId) {
      handleAssignSpell(charId, spellId);
    }
  };

  const drinkPotion = (char: Character, item: Item & { character_item_id?: number }) => {
    if (!item.character_item_id) return;
    handleUseCharacterItem(char.id, item.character_item_id);
  };

  const repairArmor = async (char: Character, item: Item) => {
    if (!item.character_item_id) return;
    try {
      const result = await repairCharacterArmor(char.id, item.character_item_id);
      setToastMessage(result.message || `${item.name} repariert`);
      window.setTimeout(() => setToastMessage(null), 3000);
      if (currentGameId) fetchGameOverview(currentGameId);
    } catch (err) {
      setToastMessage(errorMessage(err, 'Rüstung konnte nicht repariert werden.'));
      window.setTimeout(() => setToastMessage(null), 4000);
    }
  };

  const endEffect = async (char: Character, effectId: number) => {
    try {
      const result = await revokeCharacterEffect(char.id, effectId);
      setToastMessage(result.message || 'Effekt beendet');
      window.setTimeout(() => setToastMessage(null), 3000);
      if (currentGameId) fetchGameOverview(currentGameId);
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Effekt konnte nicht beendet werden');
      window.setTimeout(() => setToastMessage(null), 4000);
    }
  };

  const applyCustomBuff = async (payload: {
    name: string;
    bonuses: BuffBonuses;
    description: string;
    icon_url?: string | null;
  }) => {
    if (!buffTarget) return;
    setBuffBusy(true);
    setBuffError(null);
    try {
      const result = await createCharacterBuff(buffTarget.id, payload);
      setBuffTarget(null);
      setToastMessage(result.message || 'Buff gesetzt');
      window.setTimeout(() => setToastMessage(null), 3000);
      if (currentGameId) fetchGameOverview(currentGameId);
    } catch (err) {
      setBuffError(errorMessage(err, 'Buff konnte nicht gesetzt werden.'));
    } finally {
      setBuffBusy(false);
    }
  };

  const triggerEffect = async (char: Character, effectId: number) => {
    try {
      const result = await triggerCharacterEffect(char.id, effectId);
      setToastMessage(result.message || 'Brennen ausgelöst');
      window.setTimeout(() => setToastMessage(null), 3000);
      if (currentGameId) fetchGameOverview(currentGameId);
    } catch (err) {
      setToastMessage(err instanceof Error ? err.message : 'Effekt konnte nicht ausgelöst werden');
      window.setTimeout(() => setToastMessage(null), 4000);
    }
  };

  const spendGold = (char: Character, item: Item & { character_item_id?: number }, count: number) => {
    if (!item.character_item_id) return;
    if (count <= 1) {
      handleUseCharacterItem(char.id, item.character_item_id, 1);
      return;
    }
    setSpendQty(1);
    setGoldToSpend({
      characterId: char.id,
      characterItemId: item.character_item_id,
      itemName: itemDisplayName(item),
      maxCount: count,
    });
  };

  const selectedVitals = selectedChar ? characterVitals(selectedChar, calculateDamage) : null;

  return (
    <div className="game-overview medieval overview-master-detail" ref={pageRef} data-gamepad-root>
      <header className="game-header">
        <h1>🎮 {gameOverview.game.name}</h1>
        <div className="header-buttons">
          <BackButton fallback={paths.masterGames} className="medieval-button">
            ← Zurück<span className="btn-extra"> zum Admin-Panel</span>
          </BackButton>
          {currentGameId && (
            <>
              <button
                type="button"
                className="medieval-button"
                data-gamepad-mode
                onClick={() => navigate(paths.masterCombat(currentGameId))}
              >
                ⚔️ Kampfmodus
              </button>
              <button
                type="button"
                className={`medieval-button combat-log-toggle${combatLogOpen ? ' is-open' : ''}`}
                data-gamepad-toggle="log"
                aria-expanded={combatLogOpen}
                aria-controls="combat-log-drawer"
                disabled={combatLogBusy}
                onClick={() => {
                  void (async () => {
                    if (combatLogOpen) {
                      setCombatLogOpen(false);
                      return;
                    }
                    setCombatLogBusy(true);
                    try {
                      const data = await fetchCombatLog(currentGameId);
                      setCombatLogEntries(data.log || []);
                      setCombatLogFights(data.fights || []);
                      setCombatLogOpen(true);
                    } catch (err) {
                      setToastMessage(errorMessage(err, 'Kampflog konnte nicht geladen werden.'));
                      window.setTimeout(() => setToastMessage(null), 4000);
                    } finally {
                      setCombatLogBusy(false);
                    }
                  })();
                }}
              >
                Kampflog{combatLogEntries.length > 0 ? ` (${combatLogEntries.length})` : ''}
              </button>
            </>
          )}
          <button
            type="button"
            className="medieval-button"
            data-gamepad-open-region="catalog"
            aria-pressed={catalogOpen}
            onClick={() => setCatalogOpen((open) => !open)}
          >
            {catalogOpen ? 'Katalog aus' : 'Katalog'}
          </button>
          <button className="medieval-button logout-button" onClick={handleLogout}>
            Ausloggen
          </button>
        </div>
      </header>

      <CombatLogDrawer
        open={combatLogOpen}
        entries={combatLogEntries}
        fights={combatLogFights}
        onClose={() => setCombatLogOpen(false)}
      />

      {catalogOpen && (
        <button
          type="button"
          className="catalog-backdrop"
          data-gamepad-overlay-backdrop="catalog"
          data-gamepad-ignore
          aria-label="Katalog schliessen"
          onClick={() => setCatalogOpen(false)}
        />
      )}
      <div className={`overview-split${catalogOpen ? ' catalog-open' : ''}`}>
        <aside className="overview-roster" data-gamepad-region="roster">
          {(gameOverview.pending_join_requests || []).length > 0 && (
            <section className="join-requests-section roster-join-requests">
              <h2>📨 Beitrittsanfragen</h2>
              <div className="join-requests-list">
                {(gameOverview.pending_join_requests || []).map((request) => (
                  <div key={request.id} className="join-request-item">
                    <span className="player-name">{request.player_name}</span>
                    <span className="request-time">{request.created_at && new Date(request.created_at).toLocaleString('de-DE')}</span>
                    <div className="request-actions">
                      <button className="accept-button" onClick={() => handleAcceptJoinRequest(request.id)}>✓ Akzeptieren</button>
                      <button className="reject-button" onClick={() => handleRejectJoinRequest(request.id)}>✗ Ablehnen</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="roster-block">
            <div className="section-header-with-actions">
              <h2>Spieler ({players.length}/{gameOverview.game.max_players})</h2>
              {characters.length > 0 && (
                <button
                  type="button"
                  className="medieval-button small-button"
                  onClick={() => setNumberEditPopup({
                    mode: 'bulk-level-up',
                    title: '📈 Alle Level Up',
                    confirmLabel: 'Level Up',
                    charName: 'Alle Charaktere',
                    value: gameOverview.level_up_skill_points ?? 5,
                    min: 0,
                    max: 99,
                  })}
                >
                  Alle Level Up
                </button>
              )}
            </div>
            {players.length === 0 ? (
              <p className="no-content">Noch keine Spieler</p>
            ) : (
              <div className="roster-list" data-gamepad-entry ref={rosterListRef}>
                {orderedPlayers.map((player) => {
                  const playerChar = characters.find((character) => character.player_id === player.id);
                  const vitals = playerChar ? characterVitals(playerChar, calculateDamage) : null;
                  const selected = player.id === selectedPlayerId;
                  return (
                    <button
                      key={player.id}
                      type="button"
                      data-testid={`roster-row-${player.id}`}
                      data-roster-id={player.id}
                      data-gamepad-roster-row
                      className={`roster-row${selected ? ' is-selected' : ''}${playerChar?.pending_level_up ? ' pending-levelup' : ''}${playerChar ? ' drop-zone' : ''}`}
                      aria-pressed={selected}
                      {...(playerChar ? bindDropHandlers((itemId, spellId) => {
                        setSelectedPlayerId(player.id);
                        assignDropped(playerChar.id, itemId, spellId);
                      }) : {})}
                      onClick={() => setSelectedPlayerId(player.id)}
                    >
                      <div className="roster-row-top">
                        <span className="roster-avatar" aria-hidden>
                          {playerChar
                            ? <ClassMark klass={playerChar.class} icon={app.classes?.find(c => c.name === playerChar.class)?.icon} />
                            : '👤'}
                        </span>
                        <strong>{playerChar ? playerChar.name : player.name}</strong>
                        <span className="roster-side">
                          {playerChar && <span className="roster-player">{player.name}</span>}
                          <span className="roster-class">{playerChar ? playerChar.class : 'Wartet'}</span>
                        </span>
                      </div>
                      {playerChar && playerChar.pending_level_up ? (
                        <span className="roster-pending">Level-up {playerChar.pending_skill_points} SP</span>
                      ) : null}
                      {vitals ? (
                        <>
                          <div
                            className={`combat-bar combat-hp roster-hp ${vitals.hpPct > 0 && vitals.hpPct <= 30 ? 'is-critical' : ''}`}
                          >
                            <div className="combat-bar-fill combat-hp-fill" style={{ width: `${vitals.hpPct}%` }} />
                            <span>❤️ Leben {vitals.currentHp}/{vitals.maxHp}</span>
                          </div>
                          <div className="combat-bar combat-mana roster-hp">
                            <div className="combat-bar-fill combat-mana-fill" style={{ width: `${vitals.manaPct}%` }} />
                            <span>✨ Mana {vitals.currentMana}/{vitals.maxMana}</span>
                          </div>
                          {vitals.armorMax > 0 && (
                            <div className="combat-bar combat-armor roster-hp" title="Rüstung ist Zusatzleben">
                              <div className="combat-bar-fill combat-armor-fill" style={{ width: `${vitals.armorPct}%` }} />
                              <span>🛡 Rüstung {vitals.armor}/{vitals.armorMax}</span>
                            </div>
                          )}
                          <div className="roster-combat-icons">
                            <span title="Schaden">
                              ⚔ Schaden {vitals.damage}
                              {vitals.damageBonus > 0 && <span className="stat-bonus">+{vitals.damageBonus}</span>}
                            </span>
                            <span title="Bewegung">
                              ➤ Bewegung {vitals.movement}
                              {vitals.movementBonus > 0 && <span className="stat-bonus">+{vitals.movementBonus}</span>}
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="roster-waiting">Noch kein Charakter</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <GameNpcsSection gameId={gameOverview.game.id} compact />
        </aside>

        <section className="overview-detail" data-testid="character-detail" data-gamepad-region="sheet" data-gamepad-parent="roster">
          {!selectedPlayer ? (
            <div className="character-detail-empty">
              <p className="no-content">Wähle links einen Spieler.</p>
            </div>
          ) : !selectedChar || !selectedVitals ? (
            <article className="character-detail-sheet">
              <header className="detail-head">
                <div className="detail-avatar" aria-hidden>👤</div>
                <div className="detail-head-main">
                  <h2>{selectedPlayer.name}</h2>
                  <p className="detail-meta">Wartet auf Charakterwahl</p>
                </div>
              </header>
              <div className="no-character-assigned">
                <span className="waiting-icon">⏳</span>
                <span>Noch kein Charakter gewählt</span>
              </div>
            </article>
          ) : (
            <article
              className="character-detail-sheet drop-zone"
              {...bindDropHandlers((itemId, spellId) => assignDropped(selectedChar.id, itemId, spellId))}
            >
              <header className="detail-head">
                <div className="detail-avatar" aria-hidden>
                  <ClassMark klass={selectedChar.class} icon={app.classes?.find(c => c.name === selectedChar.class)?.icon} />
                </div>
                <div className="detail-head-main">
                  <div className="detail-title-row">
                    <h2>{selectedChar.name}</h2>
                    <span className="sheet-player-name">{selectedPlayer.name}</span>
                  </div>
                  <p className="detail-meta">
                    {selectedChar.class} · Stufe {selectedChar.level}
                  </p>
                  {selectedChar.pending_level_up && (
                    <span className="combat-ribbon">Level-up {selectedChar.pending_skill_points} SP</span>
                  )}
                </div>
                <div className="detail-admin-row" data-gamepad-zone="actions" tabIndex={-1}>
                  <div className="add-dropdown-wrapper">
                    <button
                      type="button"
                      className="add-btn"
                      onClick={() => { setAddItemOpen(!addItemOpen); setAddSpellOpen(false); }}
                    >
                      Item
                    </button>
                    {addItemOpen && (
                      <div className="add-dropdown">
                        {assignableCatalogItems.length === 0 ? (
                          <div className="add-dropdown-empty">Alle Items zugewiesen</div>
                        ) : (
                          assignableCatalogItems.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              className="add-dropdown-item"
                              onClick={() => { handleAssignItem(selectedChar.id, item.id); setAddItemOpen(false); }}
                            >
                              <Pictogram iconUrl={item.icon_url} type={item.type} name={item.name} kind="item" className="add-dropdown-icon" />
                              {item.name}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <div className="add-dropdown-wrapper">
                    <button
                      type="button"
                      className="add-btn"
                      onClick={() => { setAddSpellOpen(!addSpellOpen); setAddItemOpen(false); }}
                    >
                      Zauber
                    </button>
                    {addSpellOpen && (
                      <div className="add-dropdown">
                        {catalogSpells.filter((spell) => !assignedSpellIds.has(spell.id)).length === 0 ? (
                          <div className="add-dropdown-empty">Alle Zauber zugewiesen</div>
                        ) : (
                          catalogSpells.filter((spell) => !assignedSpellIds.has(spell.id)).map((spell) => (
                            <button
                              key={spell.id}
                              type="button"
                              className="add-dropdown-item"
                              onClick={() => { handleAssignSpell(selectedChar.id, spell.id); setAddSpellOpen(false); }}
                            >
                              <Pictogram iconUrl={spell.icon_url} name={spell.name} kind="spell" className="add-dropdown-icon" />
                              {spell.name} (✨{spell.mana_cost})
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="action-btn level-up-btn"
                    onClick={() => setNumberEditPopup({
                      mode: 'level-up',
                      title: '📈 Level Up',
                      confirmLabel: 'Level Up',
                      characterId: selectedChar.id,
                      charName: selectedChar.name,
                      value: gameOverview.level_up_skill_points ?? 5,
                      min: 0,
                      max: 99,
                    })}
                  >
                    Level
                  </button>
                  <button
                    type="button"
                    className="action-btn set-level-btn"
                    onClick={() => setNumberEditPopup({
                      mode: 'set-level',
                      title: '🎯 Stufe setzen',
                      confirmLabel: 'Übernehmen',
                      characterId: selectedChar.id,
                      charName: selectedChar.name,
                      value: selectedChar.level,
                      min: 1,
                      max: 99,
                    })}
                  >
                    Stufe
                  </button>
                  <button
                    type="button"
                    className="action-btn reset-btn"
                    onClick={() => window.confirm(`Level von ${selectedChar.name} auf 1 zuruecksetzen?`) && handleResetLevel(selectedChar.id)}
                  >
                    Reset
                  </button>
                </div>
              </header>

              <div className="detail-vitals-stats">
                <div className="character-vitals-compact" data-gamepad-zone="vitals" tabIndex={-1}>
                  <div
                    className={`combat-bar combat-hp vital-bar-compact vital-bar-clickable ${selectedVitals.hpPct > 0 && selectedVitals.hpPct <= 30 ? 'is-critical' : ''}`}
                    title={`Klicken: Leben ändern. Formel: ${selectedChar.health_formula || 'Standard'}`}
                    onClick={() => setVitalEditPopup({
                      type: 'health',
                      characterId: selectedChar.id,
                      value: selectedVitals.currentHp,
                      max: selectedVitals.maxHp,
                      charName: selectedChar.name,
                    })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setVitalEditPopup({
                          type: 'health',
                          characterId: selectedChar.id,
                          value: selectedVitals.currentHp,
                          max: selectedVitals.maxHp,
                          charName: selectedChar.name,
                        });
                      }
                    }}
                  >
                    <div className="combat-bar-fill combat-hp-fill" style={{ width: `${selectedVitals.hpPct}%` }} />
                    <span>❤️ Leben {selectedVitals.currentHp}/{selectedVitals.maxHp}{selectedChar.health_formula ? ' 📐' : ''}</span>
                  </div>
                  {selectedVitals.armorMax > 0 && (
                    <div
                      className="combat-bar combat-armor vital-bar-compact"
                      title="Zusatzleben. Schaden: Buff, dann schwächste Rüstung, dann Leben."
                    >
                      <div className="combat-bar-fill combat-armor-fill" style={{ width: `${selectedVitals.armorPct}%` }} />
                      <span>🛡 Rüstung {selectedVitals.armor}/{selectedVitals.armorMax}</span>
                    </div>
                  )}
                  <div
                    className="combat-bar combat-mana vital-bar-compact vital-bar-clickable"
                    title={`Klicken: Mana ändern. Formel: ${selectedChar.mana_formula || 'Standard'}`}
                    onClick={() => setVitalEditPopup({
                      type: 'mana',
                      characterId: selectedChar.id,
                      value: selectedVitals.currentMana,
                      max: selectedVitals.maxMana,
                      charName: selectedChar.name,
                    })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setVitalEditPopup({
                          type: 'mana',
                          characterId: selectedChar.id,
                          value: selectedVitals.currentMana,
                          max: selectedVitals.maxMana,
                          charName: selectedChar.name,
                        });
                      }
                    }}
                  >
                    <div className="combat-bar-fill combat-mana-fill" style={{ width: `${selectedVitals.manaPct}%` }} />
                    <span>✨ Mana {selectedVitals.currentMana}/{selectedVitals.maxMana}{selectedChar.mana_formula ? ' 📐' : ''}</span>
                  </div>
                </div>
                <div className="stat-chips">
                  {(() => {
                    const sources = bonusSources(selectedChar);
                    const baseDmg = calculateDamage(selectedChar.damage_formula, selectedChar, '5+strength*0.5');
                    const bonus = itemDamageBonus(sources);
                    const total = totalAttackDamage(baseDmg, sources);
                    return (
                      <span className="stat-chip" title={`Formel: ${selectedChar.damage_formula || '5+strength*0.5'} = ${baseDmg}${bonus ? ` · Items +${bonus}` : ''} → ${total}`}>
                        <span className="stat-chip-icon" aria-hidden>⚔</span>
                        Schaden {total}{bonus > 0 && <span className="stat-bonus">+{bonus}</span>}
                      </span>
                    );
                  })()}
                  {(() => {
                    const sources = bonusSources(selectedChar);
                    const baseMove = calculateDamage(selectedChar.movement_formula, selectedChar, '3+dexterity*0.1');
                    const moveBonus = itemMovementBonus(sources);
                    const totalMove = totalMovement(baseMove, sources);
                    return (
                      <span className="stat-chip" title={`Formel: ${selectedChar.movement_formula || '3+dexterity*0.1'} = ${baseMove}${moveBonus ? ` · Items +${moveBonus}` : ''} → ${totalMove}`}>
                        <span className="stat-chip-icon" aria-hidden>➤</span>
                        Bewegung {totalMove}{moveBonus > 0 && <span className="stat-bonus">+{moveBonus}</span>}
                      </span>
                    );
                  })()}
                  <span className="stat-chip">
                    <span className="stat-chip-icon" aria-hidden>💪</span>
                    Stärke {selectedChar.strength || 10}
                  </span>
                  <span className="stat-chip">
                    <span className="stat-chip-icon" aria-hidden>🎯</span>
                    Geschick {selectedChar.dexterity || 10}
                  </span>
                  <span className="stat-chip">
                    <span className="stat-chip-icon" aria-hidden>🧠</span>
                    Intelligenz {selectedChar.intelligence || 10}
                  </span>
                  <span className="stat-chip">
                    <span className="stat-chip-icon" aria-hidden>❤️</span>
                    Konstitution {selectedChar.constitution || 10}
                  </span>
                  {selectedVitals.armorMax > 0 && (
                    <span
                      className="stat-chip"
                      title="Zusatzleben. Schaden geht zuerst auf Buffs, dann auf das schwächste Rüstungsteil, danach auf Leben."
                    >
                      <span className="stat-chip-icon" aria-hidden>🛡</span>
                      Rüstung {selectedVitals.armor}/{selectedVitals.armorMax}
                    </span>
                  )}
                  {(selectedChar.skill_points || 0) > 0 && (
                    <span className="stat-chip">
                      <span className="stat-chip-icon" aria-hidden>✨</span>
                      {selectedChar.skill_points} SP
                    </span>
                  )}
                </div>
              </div>

              <div className="kit-section" data-gamepad-zone="spells" tabIndex={-1}>
                <h3 className="kit-section-title">Zauber</h3>
                {(selectedChar.spells || []).length === 0 ? (
                  <p className="no-content">Keine Zauber</p>
                ) : (
                  <div className="kit-card-grid">
                    {(selectedChar.spells || []).map((spell: Spell) => {
                      const evaluatedEffect = evaluateFormulasInText(spell.effect, selectedChar);
                      const parts = kitEffectParts(evaluatedEffect || spell.effect || '', spell.name, spell.description);
                      const effectLine = readableSpellEffect(evaluatedEffect, parts.caption);
                      const cost = spell.mana_cost ?? 0;
                      const canCast = (selectedVitals?.currentMana ?? 0) >= cost;
                      return (
                        <div key={spell.id} className="kit-card" data-gamepad-item data-gamepad-tile data-gamepad-tile-key={`spell-${spell.id}`} tabIndex={-1}>
                          <button
                            type="button"
                            className="kit-card-main"
                            title={spell.description || spell.effect}
                            onClick={() => setTooltipPopup({
                              content: [spell.name, spell.description, spell.effect && `Effekt: ${evaluatedEffect}`, `Mana ${cost}`].filter(Boolean).join('\n'),
                            })}
                          >
                            <Pictogram iconUrl={spell.icon_url} name={spell.name} kind="spell" className="kit-card-icon" />
                            <span className="kit-card-copy">
                              <span className="kit-card-title">
                                <strong>{itemDisplayName(spell)}</strong>
                                {parts.kind && (
                                  <KitChipView chip={{ kind: parts.kind, icon: parts.icon, label: parts.label }} />
                                )}
                                <span className="kit-card-chip is-mana">
                                  <span aria-hidden>✨</span> Mana {cost}
                                </span>
                              </span>
                              {effectLine && effectLine !== parts.caption ? <span className="kit-card-effect">{effectLine}</span> : null}
                            </span>
                          </button>
                          <div className="kit-card-foot">
                            <button
                              type="button"
                              className="spell-cast-btn kit-card-cast"
                              data-gamepad-primary
                              data-gamepad-action="cast"
                              disabled={!canCast}
                              title={canCast ? `${spell.name} wirken (−${cost} Mana)` : `Nicht genug Mana (benötigt ${cost})`}
                              aria-label={canCast ? `${itemDisplayName(spell)} wirken` : `Nicht genug Mana für ${itemDisplayName(spell)}`}
                              onClick={() => castSpell(selectedChar, spell)}
                            >
                              Wirken
                            </button>
                            <button
                              type="button"
                              className="remove-spell-btn"
                              onClick={() => handleRemoveSpell(selectedChar.id, spell.id)}
                              title="Zauber entfernen"
                              aria-label={`${itemDisplayName(spell)} entfernen`}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="kit-section" data-gamepad-zone="items" tabIndex={-1}>
                <h3 className="kit-section-title">Items</h3>
                {(selectedChar.items || []).length === 0 ? (
                  <p className="no-content">Keine Items</p>
                ) : (
                  <div className="kit-card-grid">
                    {stackInventoryItems(selectedChar.items || []).map(({ item, count }, idx: number) => {
                      const potion = isConsumablePotion(item);
                      const gold = isCurrencyItem(item);
                      const restore = isPotionItem(item) && selectedVitals
                        ? potionRestoreSummary(item, selectedVitals.maxHp, selectedVitals.maxMana)
                        : null;
                      const hpFull = !restore?.health || (selectedVitals?.currentHp ?? 0) >= (selectedVitals?.maxHp ?? 0);
                      const manaFull = !restore?.mana || (selectedVitals?.currentMana ?? 0) >= (selectedVitals?.maxMana ?? 0);
                      const canDrink = potion && Boolean(item.character_item_id) && (potionHasLastingEffect(item) || !(hpFull && manaFull));
                      const chips = itemKitChips(item, selectedVitals);
                      const extra = itemKitExtra(item, selectedVitals);
                      const chipText = chips.map((chip) => chip.label).join(', ');
                      const broken = isArmorBroken(item);
                      const damaged = isArmorDamaged(item);
                      return (
                      <div key={item.character_item_id || `${item.id}-${idx}`} className={`kit-card${broken ? ' is-broken' : ''}`} data-gamepad-item data-gamepad-tile data-gamepad-tile-key={`item-${item.character_item_id || item.id}`} tabIndex={-1}>
                        <button
                          type="button"
                          className="kit-card-main"
                          title={item.effect}
                          onClick={() => setTooltipPopup({
                            content: [item.name, item.description, item.effect && `Effekt: ${item.effect}`].filter(Boolean).join('\n'),
                          })}
                        >
                          <Pictogram iconUrl={item.icon_url} type={item.type} name={item.name} kind="item" className="kit-card-icon" />
                          <span className="kit-card-copy">
                            <span className="kit-card-title">
                              <strong>
                                {itemDisplayName(item)}
                                {count > 1 && <span className="stack-badge" aria-label={`${count} Stück`}>×{count}</span>}
                              </strong>
                              {chips.map((chip) => (
                                <KitChipView key={`${chip.kind}-${chip.label}`} chip={chip} />
                              ))}
                            </span>
                            {extra && extra !== chipText ? <span className="kit-card-effect">{extra}</span> : null}
                          </span>
                          {broken && <span className="kit-card-stamp" aria-hidden>Kaputt</span>}
                        </button>
                        <div className="kit-card-foot">
                          {potion && (
                            <button
                              type="button"
                              className="spell-cast-btn kit-card-cast"
                              data-gamepad-primary
                              data-gamepad-action="drink"
                              disabled={!canDrink}
                              title={canDrink ? `${item.name} trinken` : (potionHasLastingEffect(item) ? `${item.name} trinken` : 'Leben und Mana sind bereits voll')}
                              aria-label={canDrink ? `${itemDisplayName(item)} trinken` : `${itemDisplayName(item)} nicht trinkbar`}
                              onClick={() => drinkPotion(selectedChar, item)}
                            >
                              Trinken
                            </button>
                          )}
                          {gold && (
                            <button
                              type="button"
                              className="spell-cast-btn kit-card-cast"
                              data-gamepad-primary
                              data-gamepad-action="spend"
                              disabled={!item.character_item_id}
                              title={`${item.name} ausgeben`}
                              aria-label={`${itemDisplayName(item)} ausgeben`}
                              onClick={() => spendGold(selectedChar, item, count)}
                            >
                              Ausgeben
                            </button>
                          )}
                          {damaged && (
                            <button
                              type="button"
                              className="kit-card-repair"
                              data-gamepad-primary
                              data-gamepad-action="repair"
                              disabled={!item.character_item_id}
                              title="Rüstung reparieren"
                              aria-label={`${itemDisplayName(item)} reparieren`}
                              onClick={() => repairArmor(selectedChar, item)}
                            >
                              Reparieren
                            </button>
                          )}
                          <button
                            type="button"
                            className="remove-item-btn"
                            onClick={() => {
                              setRemoveQty(1);
                              setItemToRemove({
                                characterId: selectedChar.id,
                                itemId: item.id,
                                characterItemId: item.character_item_id,
                                itemName: itemDisplayName(item),
                                charName: selectedChar.name,
                                maxCount: count,
                              });
                            }}
                            title="Item entfernen"
                            aria-label={`${itemDisplayName(item)} entfernen`}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="kit-section">
                <div className="kit-section-head">
                  <h3 className="kit-section-title">Aktive Effekte</h3>
                  <button
                    type="button"
                    className="medieval-button small-button"
                    onClick={() => {
                      setBuffError(null);
                      setBuffTarget(selectedChar);
                    }}
                  >
                    + Buff
                  </button>
                </div>
                {(selectedChar.effects || []).length === 0 ? (
                  <p className="no-content">Keine aktiven Trank-, Zauber- oder Custom-Buffs</p>
                ) : (
                  <div className="kit-card-grid">
                    {(selectedChar.effects || []).map((effect) => (
                      <div key={effect.id} className={`kit-card${isBurnEffect(effect) ? ' has-burn' : ''}`}>
                        <button
                          type="button"
                          className="kit-card-main"
                          title={effect.effect}
                          onClick={() => setTooltipPopup({
                            content: [effect.name, effect.description, effect.effect && `Effekt: ${effect.effect}`].filter(Boolean).join('\n'),
                          })}
                        >
                          <Pictogram
                            iconUrl={effect.icon_url}
                            type={effect.source === 'spell' ? 'Schriftrolle' : effect.source === 'admin' ? 'Sonstiges' : 'Trank'}
                            kind={effect.source === 'spell' ? 'spell' : 'item'}
                            className="kit-card-icon"
                          />
                          <span className="kit-card-copy">
                            <span className="kit-card-title">
                              <strong>{effect.name}</strong>
                              {(() => {
                                const tagged = itemKitChips({
                                  id: effect.id,
                                  name: effect.name,
                                  type: effect.type || 'Effekt',
                                  description: effect.description || '',
                                  effect: effect.effect || '',
                                });
                                const parts = kitEffectParts(effect.effect || '', effect.name, effect.description);
                                const chips = tagged.length
                                  ? tagged
                                  : parts.kind
                                    ? [{ kind: parts.kind, icon: parts.icon, label: parts.label }]
                                    : [];
                                return chips.map((chip) => (
                                  <KitChipView key={`${effect.id}-${chip.kind}-${chip.label}`} chip={chip} />
                                ));
                              })()}
                            </span>
                            {effect.effect ? <span className="kit-card-effect">{effect.effect}</span> : null}
                          </span>
                        </button>
                        <div className="kit-card-foot">
                          {isBurnEffect(effect) && (
                            <button
                              type="button"
                              className="medieval-button small-button combat-burn-btn kit-card-burn"
                              onClick={() => void triggerEffect(selectedChar, effect.id)}
                              title="Brennen auslösen"
                              aria-label={`${effect.name} auslösen`}
                            >
                              🔥 Schaden auslösen
                              {burnTriggerAmount(effect) > 0 ? ` ${burnTriggerAmount(effect)}` : ''}
                            </button>
                          )}
                          {isRepeatHeal(effect) && (
                            <button
                              type="button"
                              className="medieval-button small-button combat-burn-btn kit-card-burn"
                              onClick={() => void triggerEffect(selectedChar, effect.id)}
                              title="Heilung auslösen"
                              aria-label={`${effect.name} heilen`}
                            >
                              Heilung auslösen
                              {repeatHealAmount(effect) > 0 ? ` ${repeatHealAmount(effect)}` : ''}
                              {repeatHealCharges(effect) > 0 ? ` × ${repeatHealCharges(effect)}` : ''}
                            </button>
                          )}
                          <button
                            type="button"
                            className="remove-item-btn"
                            onClick={() => void endEffect(selectedChar, effect.id)}
                            title="Effekt beenden"
                            aria-label={`${effect.name} beenden`}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </article>
          )}
        </section>

        {catalogOpen && (
          <aside
            className="catalog-drawer"
            data-testid="catalog-drawer"
            data-gamepad-region="catalog"
            data-gamepad-overlay="catalog"
            data-gamepad-parent="sheet"
            aria-label="Items und Zauber"
          >
            <header className="catalog-drawer-head">
              <h2>Katalog</h2>
              <button type="button" className="catalog-drawer-close" data-gamepad-dismiss onClick={() => setCatalogOpen(false)} aria-label="Katalog schliessen">
                ✕
              </button>
            </header>
            <p className="section-hint">
              {selectedChar
                ? `Klicken weist ${selectedChar.name} zu. Tränke und Gold dürfen mehrfach liegen. Ziehen auf die Liste geht auch.`
                : 'Wähle links einen Charakter, dann klicken oder ziehen zum Zuweisen.'}
            </p>
            <h3 className="kit-section-title">Items</h3>
            {catalogItems.length === 0 ? (
              <p className="no-content">Noch keine Items</p>
            ) : (
              <div className="catalog-tile-grid" data-gamepad-entry>
                {catalogItems.map((item) => {
                  const stackable = isStackableItem(item);
                  const owned = selectedChar ? assignedGearIds.has(item.id) : false;
                  const stackCount = stackable
                    ? (selectedChar?.items || []).filter((ownedItem) => ownedItem.id === item.id).length
                    : 0;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`catalog-tile${owned ? ' is-owned' : ''}`}
                      data-gamepad-item
                      data-gamepad-primary
                      aria-label={itemDisplayName(item)}
                      draggable
                      disabled={owned || !selectedChar}
                      title={owned ? 'Bereits zugewiesen' : itemKitCaption(item)}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('itemId', item.id.toString());
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={() => { if (!owned && selectedChar) handleAssignItem(selectedChar.id, item.id); }}
                    >
                      <Pictogram iconUrl={item.icon_url} type={item.type} name={item.name} kind="item" className="catalog-tile-icon" />
                      <span className="catalog-tile-name">{itemDisplayName(item)}</span>
                      <span className="catalog-tile-meta">
                        {formatItemCost(item)}
                        {itemKitChips(item).map((chip) => ` ${chip.icon} ${chip.label}`).join('')}
                        {stackCount > 0 ? ` ×${stackCount}` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <h3 className="kit-section-title">Zauber</h3>
            {catalogSpells.length === 0 ? (
              <p className="no-content">Noch keine Zauber</p>
            ) : (
              <div className="catalog-tile-grid" data-gamepad-entry>
                {catalogSpells.map((spell) => {
                  const owned = selectedChar ? assignedSpellIds.has(spell.id) : false;
                  const evaluatedEffect = selectedChar
                    ? evaluateFormulasInText(spell.effect, selectedChar)
                    : spell.effect;
                  const parts = kitEffectParts(evaluatedEffect || spell.effect || '', spell.name, spell.description);
                  return (
                    <button
                      key={spell.id}
                      type="button"
                      className={`catalog-tile${owned ? ' is-owned' : ''}`}
                      data-gamepad-item
                      data-gamepad-primary
                      aria-label={itemDisplayName(spell)}
                      draggable
                      disabled={owned || !selectedChar}
                      title={owned ? 'Bereits zugewiesen' : (parts.caption ? `${spell.name} · ${parts.caption}` : spell.name)}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('spellId', spell.id.toString());
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={() => { if (!owned && selectedChar) handleAssignSpell(selectedChar.id, spell.id); }}
                    >
                      <Pictogram iconUrl={spell.icon_url} name={spell.name} kind="spell" className="catalog-tile-icon" />
                      <span className="catalog-tile-name">{itemDisplayName(spell)}</span>
                      <span className="catalog-tile-meta">
                        {parts.kind ? `${parts.icon} ${parts.label} ` : ''}
                        ✨ Mana {spell.mana_cost}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </aside>
        )}
      </div>

      {!catalogOpen && (
        <button type="button" className="catalog-handle" aria-label="Katalog öffnen" onClick={() => setCatalogOpen(true)}>
          Katalog
        </button>
      )}

      {tooltipPopup && (
        <div className="tooltip-popup-overlay" data-gamepad-overlay="modal" onClick={() => setTooltipPopup(null)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Escape' && setTooltipPopup(null)} aria-label="Tooltip schliessen">
          <div className="tooltip-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="tooltip-popup-body">
              {tooltipPopup.content.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            <button type="button" className="tooltip-popup-close" data-gamepad-dismiss onClick={() => setTooltipPopup(null)}>Schliessen</button>
          </div>
        </div>
      )}

      {buffTarget && (
        <CustomBuffDialog
          characterName={buffTarget.name}
          busy={buffBusy}
          error={buffError}
          onCancel={() => {
            if (buffBusy) return;
            setBuffTarget(null);
            setBuffError(null);
          }}
          onSubmit={payload => void applyCustomBuff(payload)}
        />
      )}

      {vitalEditPopup && (
        <div
          className="vital-edit-popup-overlay"
          data-gamepad-overlay="modal"
          onClick={() => setVitalEditPopup(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="vital-edit-title"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setVitalEditPopup(null);
          }}
        >
          <div className="vital-edit-popup-content vital-edit-popup-slider" onClick={(e) => e.stopPropagation()}>
            <h2 id="vital-edit-title" className="vital-edit-popup-title">
              {vitalEditPopup.type === 'health' ? '❤️ HP ändern' : '✨ Mana ändern'}
            </h2>
            <p className="vital-edit-char-name">{vitalEditPopup.charName}</p>
            <span className="vital-edit-value-display">
              {vitalEditPopup.value} <span className="vital-edit-max">/ {vitalEditPopup.max}</span>
            </span>
            <div className="vital-edit-slider-row">
              <button
                type="button"
                className="vital-edit-btn vital-edit-btn-minus"
                onClick={() => setVitalEditPopup((p) => (p ? { ...p, value: Math.max(0, p.value - 1) } : null))}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.preventDefault();
                  beginHoldRepeat(() => setVitalEditPopup((p) => (p ? { ...p, value: Math.max(0, p.value - 1) } : null)));
                }}
                onPointerUp={stopHoldRepeat}
                onPointerCancel={stopHoldRepeat}
                onPointerLeave={stopHoldRepeat}
                onContextMenu={(e) => e.preventDefault()}
                aria-label="Wert verringern"
              >
                −
              </button>
              <div className="vital-edit-slider-track">
                <div className="vital-edit-slider-bar" aria-hidden>
                  <div
                    className={`vital-edit-slider-fill ${vitalEditPopup.type === 'health' ? 'is-health' : 'is-mana'}`}
                    style={{ width: `${vitalEditPopup.max > 0 ? (vitalEditPopup.value / vitalEditPopup.max) * 100 : 0}%` }}
                  />
                </div>
                <input
                  type="range"
                  className="vital-edit-slider"
                  min={0}
                  max={vitalEditPopup.max}
                  step={1}
                  value={vitalEditPopup.value}
                  aria-label={vitalEditPopup.type === 'health' ? 'HP' : 'Mana'}
                  aria-valuetext={`${vitalEditPopup.value} von ${vitalEditPopup.max}`}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setVitalEditPopup((p) => (p ? { ...p, value: Number.isFinite(next) ? next : p.value } : null));
                  }}
                />
              </div>
              <button
                type="button"
                className="vital-edit-btn vital-edit-btn-plus"
                onClick={() => setVitalEditPopup((p) => (p ? { ...p, value: Math.min(p.max, p.value + 1) } : null))}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.preventDefault();
                  beginHoldRepeat(() => setVitalEditPopup((p) => (p ? { ...p, value: Math.min(p.max, p.value + 1) } : null)));
                }}
                onPointerUp={stopHoldRepeat}
                onPointerCancel={stopHoldRepeat}
                onPointerLeave={stopHoldRepeat}
                onContextMenu={(e) => e.preventDefault()}
                aria-label="Wert erhoehen"
              >
                +
              </button>
            </div>
            <div className="vital-edit-slider-ends" aria-hidden>
              <span>0</span>
              <span>{vitalEditPopup.max}</span>
            </div>
            <div className="vital-edit-popup-actions">
              <button type="button" className="vital-edit-btn-cancel" data-gamepad-dismiss onClick={() => setVitalEditPopup(null)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="vital-edit-btn-fill"
                onClick={() => {
                  if (vitalEditPopup.type === 'health') {
                    handleSetHealthMana(vitalEditPopup.characterId, vitalEditPopup.max, null);
                  } else {
                    handleSetHealthMana(vitalEditPopup.characterId, null, vitalEditPopup.max);
                  }
                  setVitalEditPopup(null);
                }}
                title={vitalEditPopup.type === 'health' ? 'HP komplett auffüllen' : 'Mana komplett auffüllen'}
              >
                Auffüllen
              </button>
              <button
                type="button"
                className="vital-edit-btn-apply"
                onClick={() => {
                  if (vitalEditPopup.type === 'health') {
                    handleSetHealthMana(vitalEditPopup.characterId, vitalEditPopup.value, null);
                  } else {
                    handleSetHealthMana(vitalEditPopup.characterId, null, vitalEditPopup.value);
                  }
                  setVitalEditPopup(null);
                }}
              >
                Übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {itemToRemove && (
        <ConfirmDialog
          title="Item entfernen?"
          message={
            itemToRemove.maxCount > 1
              ? `Wie viele „${itemToRemove.itemName}“ von ${itemToRemove.charName} entfernen?`
              : `„${itemToRemove.itemName}“ von ${itemToRemove.charName} entfernen?`
          }
          confirmLabel={itemToRemove.maxCount > 1 && removeQty > 1 ? `${removeQty} entfernen` : 'Entfernen'}
          danger
          onCancel={() => setItemToRemove(null)}
          onConfirm={() => {
            handleRemoveItem(
              itemToRemove.characterId,
              itemToRemove.itemId,
              itemToRemove.characterItemId,
              itemToRemove.maxCount > 1 ? removeQty : 1,
            );
            setItemToRemove(null);
          }}
        >
          {itemToRemove.maxCount > 1 && (
            <QuantityField value={removeQty} max={itemToRemove.maxCount} onChange={setRemoveQty} />
          )}
        </ConfirmDialog>
      )}

      {goldToSpend && (
        <ConfirmDialog
          title={`${goldToSpend.itemName} ausgeben?`}
          message={`Wie viel „${goldToSpend.itemName}“ ausgeben?`}
          confirmLabel={spendQty > 1 ? `${spendQty} ausgeben` : 'Ausgeben'}
          onCancel={() => setGoldToSpend(null)}
          onConfirm={() => {
            handleUseCharacterItem(goldToSpend.characterId, goldToSpend.characterItemId, spendQty);
            setGoldToSpend(null);
          }}
        >
          <QuantityField value={spendQty} max={goldToSpend.maxCount} onChange={setSpendQty} />
        </ConfirmDialog>
      )}

      {numberEditPopup && (
        <div
          className="vital-edit-popup-overlay"
          data-gamepad-overlay="modal"
          onClick={() => setNumberEditPopup(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="number-edit-title"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setNumberEditPopup(null);
          }}
        >
          <div className="vital-edit-popup-content" onClick={(e) => e.stopPropagation()}>
            <h2 id="number-edit-title" className="vital-edit-popup-title">
              {numberEditPopup.title}
            </h2>
            <p className="vital-edit-char-name">{numberEditPopup.charName}</p>
            <p className="number-edit-label">
              {numberEditPopup.mode === 'set-level' ? 'Neues Level' : 'Skill Points'}
            </p>
            <div className="vital-edit-value-row">
              <button
                type="button"
                className="vital-edit-btn vital-edit-btn-minus"
                onClick={() => setNumberEditPopup((popup) => popup ? { ...popup, value: Math.max(popup.min, popup.value - 1) } : null)}
                aria-label="Wert verringern"
              >
                −
              </button>
              <input
                className="vital-edit-value-input"
                type="number"
                min={numberEditPopup.min}
                max={numberEditPopup.max}
                value={numberEditPopup.value}
                aria-label={numberEditPopup.mode === 'set-level' ? 'Neues Level' : 'Skill Points'}
                onChange={(e) => {
                  const next = parseInt(e.target.value, 10);
                  setNumberEditPopup((popup) => {
                    if (!popup) return null;
                    if (Number.isNaN(next)) return { ...popup, value: popup.min };
                    return { ...popup, value: Math.min(popup.max, Math.max(popup.min, next)) };
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmNumberEdit(numberEditPopup);
                  }
                }}
              />
              <button
                type="button"
                className="vital-edit-btn vital-edit-btn-plus"
                onClick={() => setNumberEditPopup((popup) => popup ? { ...popup, value: Math.min(popup.max, popup.value + 1) } : null)}
                aria-label="Wert erhoehen"
              >
                +
              </button>
            </div>
            <div className="vital-edit-popup-actions">
              <button type="button" className="vital-edit-btn-cancel" data-gamepad-dismiss onClick={() => setNumberEditPopup(null)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="vital-edit-btn-apply"
                onClick={() => confirmNumberEdit(numberEditPopup)}
              >
                {numberEditPopup.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
